/* <i80-flow>: an i80 component of our own (not in Helios), built only from i80 tokens.
   An illustrative, draggable process flow: a palette of block templates on the left, a
   canvas of soft cards and the connectors between them on the right.

   The look (made for i80.dk articles and for zPlan explaining a process): soft rounded
   cards with a lot of air, calm neutral surfaces, and one accent colour that only appears
   where it means something — a marked node and the connector leading into it. Each card
   shows an icon and a label; its detail opens on hover, focus or selection. */
.i80-flow {
  --i80-flow-accent-tint: var(--i80-color-palette-red-100);
  --i80-flow-accent-ink: var(--i80-color-palette-red-500);
  --i80-flow-accent-edge: var(--i80-color-palette-red-300);
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  color: var(--i80-color-foreground-primary);
  font-family: var(--i80-typography-font-stack-text);
}

@media (max-width: 640px) {
  .i80-flow {
    grid-template-columns: 1fr;
  }
}

/* --- palette --- */
.i80-flow__palette {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-content: start;
  width: 184px;
  padding: 12px;
  background: var(--i80-color-surface-faint);
  border-radius: var(--i80-border-radius-large);
}

@media (max-width: 640px) {
  .i80-flow__palette {
    flex-flow: row wrap;
    width: auto;
  }
}

.i80-flow__palette-title {
  margin: 2px 4px 2px;
  color: var(--i80-color-foreground-faint);
  font-weight: var(--i80-typography-font-weight-medium);
  font-size: var(--i80-typography-body-100-font-size);
  line-height: var(--i80-typography-body-100-line-height);
}

/* --- canvas --- */
.i80-flow__canvas {
  position: relative;
  overflow: auto;
  min-height: 240px;
  background-color: var(--i80-color-surface-primary);
  /* A whisper of a grid: light dots, so the surface reads as a working space, not a pattern. */
  background-image: radial-gradient(var(--i80-color-palette-neutral-200) 1.2px, transparent 1.2px);
  background-size: var(--i80-flow-grid, 16px) var(--i80-flow-grid, 16px);
  background-position: calc(var(--i80-flow-grid, 16px) / 2) calc(var(--i80-flow-grid, 16px) / 2);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: var(--i80-border-radius-large);
  box-shadow: var(--i80-elevation-inset-box-shadow);
}

.i80-flow__canvas:focus-visible {
  outline: 3px solid var(--i80-color-focus-action-external);
  outline-offset: -3px;
}

.i80-flow__surface {
  position: relative;
  min-width: 100%;
  min-height: 100%;
}

.i80-flow__edges {
  position: absolute;
  inset: 0;
  overflow: visible;
  pointer-events: none;
}

.i80-flow__empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-200-font-size);
  text-align: center;
  pointer-events: none;
}

/* --- the soft card: a palette block, and the card inside each node, share the look --- */
.i80-flow__block,
.i80-flow__node-card {
  display: flex;
  gap: 12px;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding: 14px 16px;
  color: var(--i80-color-foreground-strong);
  font: inherit;
  text-align: left;
  background: var(--i80-color-surface-primary);
  border: 0;
  border-radius: 18px;
  box-shadow: var(--i80-elevation-low-box-shadow);
  cursor: grab;
  transition: box-shadow 140ms ease, transform 140ms ease;
}

.i80-flow__block:hover {
  box-shadow: var(--i80-elevation-mid-box-shadow);
  transform: translateY(-1px);
}

/* The node is a positioned wrapper. Its card carries the look; the info button and the port
   sit over the card as siblings, so neither is a button nested inside a button. */
.i80-flow__node {
  position: absolute;
  min-width: 132px;
  max-width: 240px;
}

.i80-flow__node-card {
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
  touch-action: none;
}

.i80-flow__node:hover .i80-flow__node-card {
  box-shadow: var(--i80-elevation-mid-box-shadow);
  transform: translateY(-2px);
}

.i80-flow__node--selected .i80-flow__node-card {
  box-shadow: var(--i80-elevation-mid-box-shadow), 0 0 0 2px var(--i80-color-border-strong);
}

.i80-flow__node--dragging { z-index: 3; }

.i80-flow__node--dragging .i80-flow__node-card,
.i80-flow__block.i80-flow__node--dragging {
  cursor: grabbing;
  box-shadow: var(--i80-elevation-high-box-shadow);
  transform: none;
}

.i80-flow__node--locked .i80-flow__node-card,
.i80-flow__node-card[aria-disabled="true"] {
  cursor: default;
}

.i80-flow__node-card:focus-visible,
.i80-flow__block:focus-visible,
.i80-flow__info:focus-visible {
  outline: 3px solid var(--i80-color-focus-action-external);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .i80-flow__node-card, .i80-flow__block { transition: none; }
}

/* A small, quiet control at the card's top corner that opens a fuller explanation. */
.i80-flow__node--has-info .i80-flow__node-card { padding-right: 46px; }
.i80-flow__info {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  color: var(--i80-color-foreground-faint);
  background: var(--i80-color-surface-primary);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: 50%;
  box-shadow: var(--i80-elevation-low-box-shadow);
  cursor: pointer;
  transition: color 140ms ease, border-color 140ms ease, transform 140ms ease;
}
.i80-flow__info .i80-icon { width: 15px; height: 15px; }
.i80-flow__info:hover {
  color: var(--i80-color-foreground-strong);
  border-color: var(--i80-color-border-strong);
  transform: scale(1.08);
}
@media (prefers-reduced-motion: reduce) {
  .i80-flow__info { transition: none; }
}

.i80-flow__head {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* The icon sits in a soft neutral chip; the colour only comes in on an accented card. */
.i80-flow__chip {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--i80-color-foreground-strong);
  background: var(--i80-color-surface-faint);
  border-radius: 13px;
}
.i80-flow__chip .i80-icon { width: 22px; height: 22px; }

.i80-flow__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }

.i80-flow__name {
  font-weight: var(--i80-typography-font-weight-semibold);
  font-size: var(--i80-typography-body-200-font-size);
  line-height: 20px;
  color: var(--i80-color-foreground-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.i80-flow__sub {
  font-size: var(--i80-typography-body-100-font-size);
  line-height: 16px;
  color: var(--i80-color-foreground-faint);
}

/* Detail is closed until you ask for it (hover, focus or selection) — progressive disclosure. */
.i80-flow__detail {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 160ms ease;
}
.i80-flow__detail > span {
  overflow: hidden;
  font-size: var(--i80-typography-body-100-font-size);
  line-height: 1.5;
  color: var(--i80-color-foreground-faint);
}
.i80-flow__node:hover .i80-flow__detail,
.i80-flow__node:focus-within .i80-flow__detail,
.i80-flow__node--selected .i80-flow__detail {
  grid-template-rows: 1fr;
}
@media (prefers-reduced-motion: reduce) {
  .i80-flow__detail { transition: none; }
}

/* The one place colour appears: a marked node and (in JS) the edge leading into it. */
.i80-flow__node--accent .i80-flow__chip,
.i80-flow__block--accent .i80-flow__chip {
  color: var(--i80-flow-accent-ink);
  background: var(--i80-flow-accent-tint);
}
.i80-flow__node--accent .i80-flow__name,
.i80-flow__block--accent .i80-flow__name {
  color: var(--i80-flow-accent-ink);
}
.i80-flow__node--accent .i80-flow__node-card {
  box-shadow: var(--i80-elevation-low-box-shadow), 0 0 0 1px var(--i80-flow-accent-edge);
}
.i80-flow__node--accent.i80-flow__node--selected .i80-flow__node-card {
  box-shadow: var(--i80-elevation-mid-box-shadow), 0 0 0 2px var(--i80-flow-accent-edge);
}
.i80-flow__node--accent .i80-flow__detail > span,
.i80-flow__node--accent .i80-flow__sub {
  color: var(--i80-flow-accent-ink);
}

/* The output port: a small dot that appears on hover or focus, kept off the canvas
   otherwise so the diagram stays clean. Grab it to draw a connector to another node. */
.i80-flow__port {
  position: absolute;
  bottom: -7px;
  left: 50%;
  width: 14px;
  height: 14px;
  padding: 0;
  transform: translateX(-50%) scale(0.5);
  opacity: 0;
  background: var(--i80-color-surface-primary);
  border: 2px solid var(--i80-color-foreground-faint);
  border-radius: 50%;
  cursor: crosshair;
  transition: opacity 140ms ease, transform 140ms ease;
}
.i80-flow__node:hover .i80-flow__port,
.i80-flow__node:focus-within .i80-flow__port,
.i80-flow__port:focus-visible,
.i80-flow__node--linking .i80-flow__port {
  opacity: 1;
  transform: translateX(-50%) scale(1);
}
.i80-flow__port:hover,
.i80-flow__port:focus-visible,
.i80-flow__node--linking .i80-flow__port {
  border-color: var(--i80-color-foreground-strong);
  outline: none;
}
@media (prefers-reduced-motion: reduce) {
  .i80-flow__port { transition: none; }
}

/* A node under the pointer (or keyboard target) while a connector is being drawn. */
.i80-flow__node--target .i80-flow__node-card {
  box-shadow: var(--i80-elevation-mid-box-shadow), 0 0 0 2px var(--i80-color-foreground-faint);
}

/* --- connectors --- */
.i80-flow__edge {
  fill: none;
  stroke: var(--i80-color-palette-neutral-300);
  stroke-width: 2;
  stroke-linecap: round;
}
.i80-flow__edge-arrow { fill: var(--i80-color-palette-neutral-300); }

.i80-flow__edge--selected { stroke: var(--i80-color-foreground-strong); }

/* The accented path flows, so the eye can follow it to the node that matters. */
.i80-flow__edge--accent {
  stroke: var(--i80-flow-accent-edge);
  stroke-width: 2.5;
  stroke-dasharray: 1 7;
  animation: i80-flow-flow 1.1s linear infinite;
}
.i80-flow__edge-arrow--accent { fill: var(--i80-flow-accent-edge); }
@keyframes i80-flow-flow { to { stroke-dashoffset: -8; } }
@media (prefers-reduced-motion: reduce) {
  .i80-flow__edge--accent { animation: none; stroke-dasharray: none; }
}

.i80-flow__edge--dashed { stroke-dasharray: 2 6; }

/* A wide invisible path over each connector so it is easy to click. */
.i80-flow__edge-hit {
  fill: none;
  stroke: transparent;
  stroke-width: 16;
  pointer-events: stroke;
  cursor: pointer;
}

.i80-flow__edge-draft {
  fill: none;
  stroke: var(--i80-color-foreground-faint);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 1 6;
}

.i80-flow__edge-label {
  padding: 2px 9px;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-100-font-size);
  line-height: 16px;
  background: var(--i80-color-surface-primary);
  border-radius: 999px;
  box-shadow: var(--i80-elevation-low-box-shadow);
  cursor: pointer;
  pointer-events: auto;
}

.i80-flow__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
